/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { BehaviorSubject } from "rxjs";

import type { Behavior } from "../state/Behavior";
import type {
  CallRecoveryDependencies,
  LocalMediaSnapshot,
} from "./CallRecoveryAdapters";
import {
  CallRecoveryAttemptError,
  isNewEscalationAttemptError,
  runCallRecoveryAttempt,
  type RunCallRecoveryAttemptOptions,
} from "./CallRecoveryPipeline";
import type {
  CallRecoveryCancellationCode,
  CallRecoveryCancelReason,
  CallRecoveryMode,
  CallRecoveryOutcome,
  CallRecoverySignal,
  CallRecoveryStage,
  CallRecoveryState,
  FailureClassification,
  FailureContext,
  FailureContextSeed,
} from "./CallRecoveryTypes";
import { createAbortError, raceWithAbort } from "./abort";

const BACKOFF_DELAYS_MS = [500, 1_000, 2_000, 5_000] as const;

/** Планировщик задержек, заменяемый fake scheduler в unit-тестах. */
export interface CallRecoveryScheduler {
  delay(milliseconds: number, signal: AbortSignal): Promise<void>;
}

/** Источник network state без прямого чтения browser globals. */
export interface CallRecoveryNetworkStateSource {
  isOnline(): boolean;
  waitUntilOnline?(signal: AbortSignal): Promise<void>;
}

/** Детерминированные зависимости самого coordinator. */
export interface CallRecoveryCoordinatorOptions {
  readonly now?: () => number;
  readonly createCycleId?: (connectionEpoch: number) => string;
  readonly scheduler?: CallRecoveryScheduler;
  readonly network?: CallRecoveryNetworkStateSource;
  readonly runAttempt?: (
    options: RunCallRecoveryAttemptOptions,
  ) => Promise<void>;
}

/** Публичный lifecycle-контракт coordinator одной call session. */
export interface CallRecoveryCoordinator {
  readonly state$: Behavior<CallRecoveryState>;
  escalate(signal: CallRecoverySignal): Promise<CallRecoveryOutcome>;
  cancel(reason: CallRecoveryCancelReason): void;
}

interface ActiveCycle {
  readonly cycleId: string;
  readonly connectionEpoch: number;
  readonly mode: CallRecoveryMode;
  readonly generation: number;
  readonly controller: AbortController;
  readonly promise: Promise<CallRecoveryOutcome>;
  readonly resolve: (outcome: CallRecoveryOutcome) => void;
  signals: readonly CallRecoverySignal[];
  signalVersion: number;
  attemptGeneration: number;
  attemptController: AbortController | null;
  retryController: AbortController | null;
  onlineWake: (() => void) | null;
  retryIndex: number;
  lastFailureSignature: string | null;
  consecutiveSameFailureCount: number;
  settled: boolean;
}

const DEFAULT_NETWORK: CallRecoveryNetworkStateSource = {
  isOnline: () => true,
};

const DEFAULT_SCHEDULER: CallRecoveryScheduler = {
  delay: async (milliseconds, signal) => {
    await new Promise<void>((resolve, reject) => {
      if (signal.aborted) {
        reject(createAbortError());
        return;
      }
      const timer = setTimeout(() => {
        signal.removeEventListener("abort", onAbort);
        resolve();
      }, milliseconds);
      const onAbort = (): void => {
        clearTimeout(timer);
        reject(createAbortError());
      };
      signal.addEventListener("abort", onAbort, { once: true });
    });
  },
};

/**
 * Единственный владелец recovery cycle для одной логической call session.
 *
 * Coordinator не знает о React и browser globals. Все технические операции
 * делегируются adapters, а повтор всегда запускает полный pipeline с Matrix.
 */
export class DefaultCallRecoveryCoordinator implements CallRecoveryCoordinator {
  private readonly stateSubject$: BehaviorSubject<CallRecoveryState>;
  public readonly state$: Behavior<CallRecoveryState>;

  private readonly now: () => number;
  private readonly createCycleId: (connectionEpoch: number) => string;
  private readonly scheduler: CallRecoveryScheduler;
  private readonly network: CallRecoveryNetworkStateSource;
  private readonly runAttempt: (
    options: RunCallRecoveryAttemptOptions,
  ) => Promise<void>;
  private activeCycle: ActiveCycle | null = null;
  private finalOutcome: Extract<
    CallRecoveryOutcome,
    { kind: "terminal" | "cancelled" }
  > | null = null;
  private generation = 0;

  public constructor(
    private readonly callSessionId: string,
    private readonly dependencies: CallRecoveryDependencies,
    options: CallRecoveryCoordinatorOptions = {},
  ) {
    this.now = options.now ?? Date.now;
    this.createCycleId =
      options.createCycleId ??
      ((connectionEpoch): string => `${callSessionId}:${connectionEpoch}`);
    this.scheduler = options.scheduler ?? DEFAULT_SCHEDULER;
    this.network = options.network ?? DEFAULT_NETWORK;
    this.runAttempt = options.runAttempt ?? runCallRecoveryAttempt;
    this.stateSubject$ = new BehaviorSubject<CallRecoveryState>({
      callSessionId,
      cycleId: null,
      connectionEpoch: 0,
      mode: null,
      attempt: 0,
      phase: "idle",
      stage: null,
      signals: [],
      lastError: null,
      startedAt: null,
      nextDelayMs: null,
      terminalOutcome: null,
      cancellationReason: null,
    });
    this.state$ = this.stateSubject$;
  }

  // eslint-disable-next-line @typescript-eslint/promise-function-async -- Все сигналы активного cycle обязаны получать тот же Promise по identity.
  public escalate(signal: CallRecoverySignal): Promise<CallRecoveryOutcome> {
    const active = this.activeCycle;
    if (active !== null) {
      active.signals = [...active.signals, signal];
      const signalClassification = this.classifySignalFailure(active, signal);
      if (signalClassification?.kind === "terminal") {
        active.controller.abort();
        active.attemptController?.abort();
        active.retryController?.abort();
        active.onlineWake?.();
        this.updateCurrentState(active, { signals: active.signals });
        this.finishTerminal(active, signalClassification);
        return active.promise;
      }
      if (signal.type === "network_online") {
        active.onlineWake?.();
      } else {
        active.signalVersion += 1;
        active.attemptController?.abort();
        active.retryController?.abort();
      }
      this.updateCurrentState(active, {
        signals: active.signals,
      });
      return active.promise;
    }

    const settled = this.getSettledOutcome();
    if (settled !== null) return Promise.resolve(settled);

    if (signal.type === "network_online") {
      const state = this.stateSubject$.value;
      return Promise.resolve({
        kind: "ignored",
        callSessionId: this.callSessionId,
        cycleId: null,
        connectionEpoch: state.connectionEpoch,
        reason: "no_active_cycle",
      });
    }

    return this.startCycle([signal]);
  }

  public cancel(reason: CallRecoveryCancelReason): void {
    const active = this.activeCycle;
    ++this.generation;
    if (active === null) {
      if (
        this.stateSubject$.value.phase === "terminal" ||
        this.stateSubject$.value.phase === "cancelled"
      ) {
        return;
      }
      try {
        this.dependencies.presentation.cancel(this.stateSubject$.value.cycleId);
      } catch {
        // Presentation cleanup не вправе блокировать явный Leave/unmount.
      }
      const outcome: Extract<CallRecoveryOutcome, { kind: "cancelled" }> = {
        kind: "cancelled",
        callSessionId: this.callSessionId,
        cycleId: this.stateSubject$.value.cycleId,
        connectionEpoch: this.stateSubject$.value.connectionEpoch,
        reason,
      };
      this.finalOutcome = outcome;
      this.stateSubject$.next({
        ...this.stateSubject$.value,
        mode: null,
        attempt: 0,
        phase: "cancelled",
        stage: null,
        lastError: null,
        startedAt: null,
        nextDelayMs: null,
        terminalOutcome: null,
        cancellationReason: reason,
      });
      return;
    }

    active.controller.abort();
    active.attemptController?.abort();
    active.retryController?.abort();
    active.onlineWake?.();
    this.finishCancelled(active, reason);
  }

  // eslint-disable-next-line @typescript-eslint/promise-function-async -- Возвращается canonical Promise, сохранённый в active cycle.
  private startCycle(
    signals: readonly CallRecoverySignal[],
  ): Promise<CallRecoveryOutcome> {
    const connectionEpoch = this.stateSubject$.value.connectionEpoch + 1;
    const cycleId = this.createCycleId(connectionEpoch);
    const generation = ++this.generation;
    const deferred = Promise.withResolvers<CallRecoveryOutcome>();
    const active: ActiveCycle = {
      cycleId,
      connectionEpoch,
      mode: "automatic_rejoin",
      generation,
      controller: new AbortController(),
      promise: deferred.promise,
      resolve: deferred.resolve,
      signals: [...signals],
      signalVersion: signals.length,
      attemptGeneration: 0,
      attemptController: null,
      retryController: null,
      onlineWake: null,
      retryIndex: 0,
      lastFailureSignature: null,
      consecutiveSameFailureCount: 0,
      settled: false,
    };
    this.activeCycle = active;
    this.stateSubject$.next({
      callSessionId: this.callSessionId,
      cycleId,
      connectionEpoch,
      mode: "automatic_rejoin",
      attempt: 0,
      phase: "recovering",
      stage: null,
      signals: active.signals,
      lastError: null,
      startedAt: this.now(),
      nextDelayMs: null,
      terminalOutcome: null,
      cancellationReason: null,
    });

    void this.runCycle(active).catch((error: unknown) => {
      // runCycle обрабатывает operational errors сам. Эта ветка защищает
      // public Promise от зависания при неожиданной программной ошибке.
      if (!this.isCurrent(active)) return;
      void this.processUnexpectedCoordinatorError(active, error);
    });
    return active.promise;
  }

  private async runCycle(active: ActiveCycle): Promise<void> {
    const mediaSnapshot: LocalMediaSnapshot =
      this.dependencies.media.captureSnapshot();
    if (!this.isCurrent(active)) return;
    this.dependencies.presentation.freeze(active.cycleId);
    const terminalSignal = active.signals
      .map((signal) => this.classifySignalFailure(active, signal))
      .find(
        (
          classification,
        ): classification is Extract<
          FailureClassification,
          { kind: "terminal" }
        > => classification?.kind === "terminal",
      );
    if (terminalSignal !== undefined) {
      this.finishTerminal(active, terminalSignal);
      return;
    }
    const prepared = await this.prepareCycle(active);
    if (!prepared) return;

    while (this.isCurrent(active)) {
      if (!this.network.isOnline()) {
        const woke = await this.waitForNetwork(active);
        if (!woke) return;
        continue;
      }

      const attemptController = new AbortController();
      active.attemptController = attemptController;
      const abortAttempt = (): void => attemptController.abort();
      active.controller.signal.addEventListener("abort", abortAttempt, {
        once: true,
      });
      const attempt = this.stateSubject$.value.attempt + 1;
      const attemptGeneration = ++active.attemptGeneration;
      const signalVersionAtStart = active.signalVersion;
      this.updateCurrentState(active, {
        attempt,
        phase: "recovering",
        stage: "matrix_sync",
        lastError: null,
        nextDelayMs: null,
      });

      let unfreezeStarted = false;
      try {
        await this.runAttempt({
          dependencies: this.dependencies,
          mediaSnapshot,
          signal: attemptController.signal,
          signalVersionAtStart,
          getSignalVersion: () => active.signalVersion,
          onStage: (stage) =>
            this.updateAttemptStage(
              active,
              attemptGeneration,
              attemptController,
              stage,
            ),
        });
        if (!this.isCurrent(active)) return;
        if (active.signalVersion !== signalVersionAtStart) {
          continue;
        }
        if (
          !this.isCurrentAttempt(active, attemptGeneration, attemptController)
        ) {
          return;
        }

        unfreezeStarted = true;
        this.dependencies.presentation.unfreeze(active.cycleId);
        if (!this.isCurrent(active)) {
          return;
        }
        if (
          active.signalVersion !== signalVersionAtStart ||
          !this.isCurrentAttempt(active, attemptGeneration, attemptController)
        ) {
          this.dependencies.presentation.freeze(active.cycleId);
          continue;
        }
        this.finishConnected(active);
        return;
      } catch (error) {
        if (!this.isCurrent(active)) return;
        let failure = error;
        if (
          active.signalVersion !== signalVersionAtStart &&
          !active.controller.signal.aborted
        ) {
          if (unfreezeStarted) {
            this.dependencies.presentation.freeze(active.cycleId);
          }
          continue;
        }
        if (unfreezeStarted && !(error instanceof CallRecoveryAttemptError)) {
          try {
            this.dependencies.presentation.freeze(active.cycleId);
          } catch (freezeError) {
            failure = new AggregateError(
              [failure, freezeError],
              "Не удалось восстановить замороженное presentation",
            );
          }
          failure = new CallRecoveryAttemptError(
            {
              stage: "verification",
              subsystem: "recovery",
              operation: "unfreeze_presentation",
            },
            failure,
          );
        }
        const shouldContinue = await this.processFailure(
          active,
          failure,
          active.signalVersion !== signalVersionAtStart,
        );
        if (!shouldContinue) return;
      } finally {
        active.controller.signal.removeEventListener("abort", abortAttempt);
        if (active.attemptController === attemptController) {
          active.attemptController = null;
        }
      }
    }
  }

  private async processFailure(
    active: ActiveCycle,
    error: unknown,
    forceImmediateRetry = false,
    allowNetworkSource = true,
  ): Promise<boolean> {
    if (!this.isCurrent(active)) return false;
    const classification = this.classifyFailure(active, error);
    this.updateCurrentState(active, {
      lastError: {
        normalizedCode: classification.normalizedCode,
        stableSignature: classification.stableSignature,
        originalError: classification.originalError,
      },
      nextDelayMs: null,
    });

    switch (classification.kind) {
      case "cancelled":
        this.finishCancelled(active, classification.cancellationCode);
        return false;
      case "terminal":
        this.finishTerminal(active, classification);
        return false;
      case "wait_for_network":
        return await this.waitForNetwork(active, allowNetworkSource);
      case "retryable": {
        if (forceImmediateRetry || isNewEscalationAttemptError(error)) {
          // Новый health-сигнал требует полного немедленного повтора с Matrix,
          // а не наказания обычным transport backoff.
          return true;
        }
        const delay =
          BACKOFF_DELAYS_MS[
            Math.min(active.retryIndex, BACKOFF_DELAYS_MS.length - 1)
          ];
        active.retryIndex += 1;
        this.updateCurrentState(active, {
          nextDelayMs: delay,
        });
        const retryController = new AbortController();
        active.retryController = retryController;
        const abortRetry = (): void => retryController.abort();
        active.controller.signal.addEventListener("abort", abortRetry, {
          once: true,
        });
        const signalVersionAtStart = active.signalVersion;
        try {
          await raceWithAbort(
            this.scheduler.delay(delay, retryController.signal),
            retryController.signal,
          );
          if (!this.isCurrent(active)) return false;
          this.updateCurrentState(active, { nextDelayMs: null });
          return true;
        } catch (error) {
          if (!this.isCurrent(active)) return false;
          if (active.signalVersion !== signalVersionAtStart) return true;
          return await this.processFailure(
            active,
            new CallRecoveryAttemptError(
              {
                stage: this.stateSubject$.value.stage ?? "verification",
                subsystem: "recovery",
                operation: "schedule_retry_delay",
              },
              error,
            ),
          );
        } finally {
          active.controller.signal.removeEventListener("abort", abortRetry);
          if (active.retryController === retryController) {
            active.retryController = null;
          }
        }
      }
    }
  }

  private async prepareCycle(active: ActiveCycle): Promise<boolean> {
    const operations: readonly {
      readonly context: FailureContextSeed;
      readonly run: () => Promise<unknown>;
      readonly onSuccess?: (result: unknown) => void;
    }[] = [
      {
        context: {
          stage: "livekit",
          subsystem: "livekit",
          operation: "disconnect_failed_epoch",
          retryableHint: "temporary_service_unavailable",
        },
        run: async () =>
          await this.dependencies.livekit.disconnectEpoch(
            active.controller.signal,
          ),
      },
      {
        context: {
          stage: "own_membership",
          subsystem: "membership",
          operation: "pause_upstream",
          retryableHint: "membership_manager_failure",
        },
        run: async () => await this.dependencies.membership.pauseUpstream(),
      },
      {
        context: {
          stage: "local_media",
          subsystem: "media",
          operation: "stop_screen_sharing",
          retryableHint: "required_track_transport_failure",
        },
        run: async () => await this.dependencies.media.stopScreenSharing(),
        onSuccess: (stopped): void => {
          if (stopped === true && this.isCurrent(active)) {
            this.dependencies.presentation.markScreenShareStopped(
              active.cycleId,
            );
          }
        },
      },
    ];

    for (const operation of operations) {
      while (this.isCurrent(active)) {
        this.updateCurrentState(active, {
          phase: "recovering",
          stage: operation.context.stage,
          nextDelayMs: null,
        });
        try {
          const result = await raceWithAbort(
            operation.run(),
            active.controller.signal,
          );
          operation.onSuccess?.(result);
          break;
        } catch (error) {
          if (!this.isCurrent(active)) return false;
          const shouldContinue = await this.processFailure(
            active,
            new CallRecoveryAttemptError(operation.context, error),
          );
          if (!shouldContinue || !this.isCurrent(active)) return false;
        }
      }
    }
    return this.isCurrent(active);
  }

  private classifyFailure(
    active: ActiveCycle,
    error: unknown,
  ): FailureClassification {
    const attemptError =
      error instanceof CallRecoveryAttemptError ? error : undefined;
    const originalError = attemptError?.cause ?? error;
    const seed: FailureContextSeed = attemptError?.context ?? {
      stage: this.stateSubject$.value.stage ?? "verification",
      subsystem: "recovery",
      operation: "unexpected_pipeline_failure",
    };
    const baseContext: Omit<FailureContext, "consecutiveSameFailureCount"> = {
      ...seed,
      networkState: this.network.isOnline() ? "online" : "offline",
      ...(active.controller.signal.aborted
        ? { cancellationReason: "unmount" as const }
        : {}),
    };
    const tentative = this.dependencies.classify(originalError, {
      ...baseContext,
      consecutiveSameFailureCount: 1,
    });
    const consecutiveSameFailureCount =
      tentative.stableSignature === active.lastFailureSignature
        ? active.consecutiveSameFailureCount + 1
        : 1;
    const classification = this.dependencies.classify(originalError, {
      ...baseContext,
      consecutiveSameFailureCount,
    });
    active.lastFailureSignature = classification.stableSignature;
    active.consecutiveSameFailureCount = consecutiveSameFailureCount;
    return classification;
  }

  /**
   * Сохраняет typed LiveKit DisconnectReason на границе health signal.
   * Иначе первый pipeline failure затёр бы исходную причину disconnect.
   */
  private classifySignalFailure(
    active: ActiveCycle,
    signal: CallRecoverySignal,
  ): FailureClassification | undefined {
    if (signal.type !== "livekit_disconnected") return undefined;
    return this.dependencies.classify(
      new Error("LiveKit завершил соединение"),
      {
        stage: "livekit",
        subsystem: "livekit",
        operation: "room_disconnected",
        livekitDisconnectReason: signal.reason,
        networkState: this.network.isOnline() ? "online" : "offline",
        consecutiveSameFailureCount: Math.max(
          1,
          active.consecutiveSameFailureCount + 1,
        ),
      },
    );
  }

  private async waitForNetwork(
    active: ActiveCycle,
    allowNetworkSource = true,
  ): Promise<boolean> {
    if (!this.isCurrent(active)) return false;
    this.updateCurrentState(active, {
      phase: "waiting_for_network",
      stage: null,
      nextDelayMs: null,
    });
    const wake = Promise.withResolvers<void>();
    active.onlineWake = wake.resolve;
    const waits: Promise<void>[] = [wake.promise];
    if (allowNetworkSource && this.network.waitUntilOnline !== undefined) {
      waits.push(this.network.waitUntilOnline(active.controller.signal));
    }
    try {
      await raceWithAbort(Promise.race(waits), active.controller.signal);
      return this.isCurrent(active);
    } catch (error) {
      if (!this.isCurrent(active)) return false;
      return await this.processFailure(
        active,
        new CallRecoveryAttemptError(
          {
            stage: "matrix_sync",
            subsystem: "recovery",
            operation: "wait_until_network_online",
            retryableHint: "temporary_service_unavailable",
          },
          error,
        ),
        false,
        false,
      );
    } finally {
      if (active.onlineWake === wake.resolve) active.onlineWake = null;
    }
  }

  private updateAttemptStage(
    active: ActiveCycle,
    attemptGeneration: number,
    attemptController: AbortController,
    stage: CallRecoveryStage,
  ): void {
    if (!this.isCurrentAttempt(active, attemptGeneration, attemptController)) {
      return;
    }
    this.updateCurrentState(active, {
      phase: stage === "verification" ? "verifying" : "recovering",
      stage,
      nextDelayMs: null,
    });
  }

  private finishConnected(active: ActiveCycle): void {
    if (!this.isCurrent(active)) return;
    const outcome: CallRecoveryOutcome = {
      kind: "connected",
      callSessionId: this.callSessionId,
      cycleId: active.cycleId,
      connectionEpoch: active.connectionEpoch,
    };
    this.stateSubject$.next({
      ...this.stateSubject$.value,
      attempt: 0,
      phase: "connected",
      stage: null,
      lastError: null,
      nextDelayMs: null,
      terminalOutcome: null,
      cancellationReason: null,
    });
    this.settle(active, outcome);
  }

  private finishTerminal(
    active: ActiveCycle,
    classification: Extract<FailureClassification, { kind: "terminal" }>,
  ): void {
    if (!this.isCurrent(active)) return;
    const outcome: Extract<CallRecoveryOutcome, { kind: "terminal" }> = {
      kind: "terminal",
      callSessionId: this.callSessionId,
      cycleId: active.cycleId,
      connectionEpoch: active.connectionEpoch,
      error: classification.terminalError,
    };
    this.stateSubject$.next({
      ...this.stateSubject$.value,
      phase: "terminal",
      stage: null,
      nextDelayMs: null,
      terminalOutcome: outcome,
      cancellationReason: null,
    });
    this.finalOutcome = outcome;
    try {
      this.dependencies.presentation.showTerminal(classification.terminalError);
    } catch {
      // Ошибка presentation не должна оживлять terminal recovery cycle.
    }
    this.settle(active, outcome);
  }

  private finishCancelled(
    active: ActiveCycle,
    reason: CallRecoveryCancellationCode,
  ): void {
    if (active.settled) return;
    const outcome: CallRecoveryOutcome = {
      kind: "cancelled",
      callSessionId: this.callSessionId,
      cycleId: active.cycleId,
      connectionEpoch: active.connectionEpoch,
      reason,
    };
    this.finalOutcome = outcome;
    this.stateSubject$.next({
      ...this.stateSubject$.value,
      attempt: 0,
      phase: "cancelled",
      stage: null,
      lastError: null,
      nextDelayMs: null,
      terminalOutcome: null,
      cancellationReason: reason,
    });
    try {
      this.dependencies.presentation.cancel(active.cycleId);
    } catch {
      // Presentation cleanup не вправе оставлять canonical Promise незавершённым.
    }
    this.settle(active, outcome);
  }

  private settle(active: ActiveCycle, outcome: CallRecoveryOutcome): void {
    if (active.settled) return;
    active.settled = true;
    if (this.activeCycle === active) this.activeCycle = null;
    active.resolve(outcome);
  }

  private getSettledOutcome(): CallRecoveryOutcome | null {
    return this.finalOutcome;
  }

  private updateCurrentState(
    active: ActiveCycle,
    patch: Partial<CallRecoveryState>,
  ): void {
    if (!this.isCurrent(active)) return;
    this.stateSubject$.next({
      ...this.stateSubject$.value,
      ...patch,
    });
  }

  private isCurrent(active: ActiveCycle): boolean {
    return (
      !active.settled &&
      this.activeCycle === active &&
      this.generation === active.generation
    );
  }

  private isCurrentAttempt(
    active: ActiveCycle,
    attemptGeneration: number,
    controller: AbortController,
  ): boolean {
    return (
      this.isCurrent(active) &&
      active.attemptGeneration === attemptGeneration &&
      active.attemptController === controller &&
      !controller.signal.aborted
    );
  }

  private async processUnexpectedCoordinatorError(
    active: ActiveCycle,
    error: unknown,
  ): Promise<void> {
    const shouldContinue = await this.processFailure(active, error);
    if (shouldContinue && this.isCurrent(active)) {
      // Неожиданная ошибка вне pipeline не может безопасно продолжить этот
      // stack. Новый полный cycle запустит следующий явный escalation.
      this.finishCancelled(active, "operation_aborted");
    }
  }
}
